import moment from 'moment'
import {isEditableRecord} from '../model/form'
import _ from 'lodash'
import unwrapModel from '../../components/unwrap-model'

import Actor from './actor'
import constants from '../../components/constants'
import user from 'scoreboard/components/user'
import globals from '../../components/globals'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, TIME, DATE_TIME, PERCENT } = DataType
import WidgetState from '../model/widget-state'
const { TEXT_ONLY } = WidgetState
import isGeographicOrRollup from '../../datasets/is-geographic-or-rollup'
const {
	SPECIFIC_VALUE,
	BLANK,
	CURRENT_DATE_TIME,
	USER_EMAIL,
	USER_NAME,
	USER_USERNAME,
	USER_ID,
	USER_IP_ADDRESS
} = constants.SET_FIELD_VALUE_TYPE

/**
 * Changes the state of a widget on the page
 */
class SetFieldValueActor extends Actor {

	act() {
		const { action, formDirector } = this.options
		const setFieldValueType = action?.setFieldValueType
		const widgetsView = formDirector?.widgetsView
		const fieldId = action?.setFieldValueFieldId
		const fieldDataType =
			this.model?.dataset?.getUnderlyingField(fieldId)?.dataType
		const record = _.get(formDirector, 'record')
		const isRecordEditable =
			isEditableRecord(fieldId, this.model.dataset, record)

		// Do not set field values when the widgetsView is not present
		// or if the record is not editable
		if (!widgetsView || !isRecordEditable) return

		// Prepare user data for pre-made values
		const userInfo = user?.model?.attributes
		let dateTimeVal = this.getDateTimeVal(fieldDataType)
		let typeToValueMap =
			this.getTypeToValueMap(fieldDataType, action, userInfo, dateTimeVal)

		let setFieldValue = ''
		if (setFieldValueType in typeToValueMap) {
			setFieldValue = typeToValueMap[setFieldValueType]
		} else {
			return // Avoid setting field value if no type is selected
		}

		// Update each widget matching the field ID
		this.updateWidgets({
			widgetsView, fieldId, formDirector,
			fieldDataType, setFieldValue, dateTimeVal, setFieldValueType
		})
	}

	getDateTimeVal(fieldDataType) {
		let dateTimeVal = new Date()

		// TIME is formatted separately from DATE_TIME and DATE
		// for time-input.js parsing
		if (fieldDataType == TIME) {
			return dateTimeVal.toTimeString()
		} else if ([DATE, DATE_TIME].includes(fieldDataType)) {
			return dateTimeVal.toISOString()
		} else {
			// Use a human-readable date string for all other field types
			return dateTimeVal.toLocaleString()
		}
	}

	getTypeToValueMap(fieldDataType, action, userInfo, dateTimeVal) {
		const typeToValueMap = {
			[SPECIFIC_VALUE]: action?.setFieldValue ?? '',
			[BLANK]: isGeographicOrRollup(fieldDataType) ?
				null : '', // Modify blank value to fit certain data types
			[CURRENT_DATE_TIME]: dateTimeVal,
			[USER_EMAIL]: userInfo?.email ?? '',
			// #56114
			// User's Name formats (i.e. First, Last; Last, First MI) are
			// calculated from the User object's label property
			[USER_NAME]: userInfo?.label ?? '',
			[USER_USERNAME]: userInfo?.username ?? '',
			[USER_ID]: userInfo?.id ?? '',
			[USER_IP_ADDRESS]: globals?.ipAddress ?? '',
		}
		return typeToValueMap
	}

	/**
	 * Takes a list of widgets and discerns how to extract the necessary info
	 * based on where the field is located in the widgets.
	 * @param widgetsView - The widgets view containing the widgets to be updated
	 * @param fieldId - The ID of the field to be updated
	 * @param formDirector - The form director for the form containing the widgets
	 * @param fieldDataType - The data type of the field to be updated
	 * @param setFieldValue - The value to be set for the field
	 * @param dateTimeVal - The date-time value to be set for the field
	 * @param setFieldValueType - The type of value to be set for the field
	 */
	updateWidgets({
		widgetsView, fieldId, formDirector,
		fieldDataType, setFieldValue, dateTimeVal, setFieldValueType
	}) {
		widgetsView?.widgetViews?.forEach(widgetView => {
			let widget = widgetView?.options?.widget

			// Check for regular fieldValueControls on field widgets
			if (widgetView?.fieldValueControl?.field?.id == fieldId) {
				this.updateWidget({
					widget, widgetsView, widgetView, fieldDataType,
					setFieldValueType, formDirector, fieldId,
					setFieldValue, dateTimeVal
				})
			}

			// Then account for fields nested within checkboxes
			if (Array.isArray(widget?.childWidgets)) {
				widget.childWidgets.forEach(childWidget => {
					if (childWidget?.fieldId == fieldId) {
						this.updateWidget({
							widget: childWidget, parentWidget: widget,
							widgetsView, widgetView, fieldDataType,
							setFieldValueType, formDirector, fieldId,
							setFieldValue, dateTimeVal
						})
					}
				})
			}
		})
	}

	/**
	 * Updates a widget and its field value based on the field data type and
	 * widget state.
	 * Also marks the record as edited if the record is set to the form
	 * director from a record list widget button action.
	 * @param widget - The widget to be updated for the set field value action
	 * @param widgetsView - The widgets view containing the widgets to be updated
	 * @param widgetView - The widget view to be updated for the set field value action
	 * @param fieldDataType - The data type of the field to be updated
	 * @param setFieldValueType - The type of value to be set for the field
	 * @param formDirector - The form director for the form containing the widget
	 * @param fieldId - The ID of the field to be updated
	 * @param setFieldValue - The value to be set for the field
	 * @param dateTimeVal - The date-time value to be set for the field
	 * @param parentWidget - The parent widget of the widget to be updated
	 */
	updateWidget({
		widget, widgetsView, widgetView, fieldDataType, setFieldValueType,
		formDirector, fieldId, setFieldValue, dateTimeVal, parentWidget
	}) {
		// Records are only marked as edited if a record is set to
		// the form director from a record list widget button action
		if (formDirector?.record?.edited) {
			// Must mark record in formDirector as edited for record to properly
			// update in the server as well as updating the UI on re-render
			const unwrappedModel = unwrapModel(formDirector?.model)
			const fieldIndex =
				_.findIndex(unwrappedModel?.dataset?.fields, field =>
					field?.id == fieldId)
			formDirector.record.edited[fieldIndex] = true
		}

		this.setFieldValue(
			widget, fieldDataType, setFieldValueType,
			formDirector, fieldId, setFieldValue
		)

		widgetsView.updateWidgetView(parentWidget || widget)

		this.modifyTextOnlyDateValue(
			widget, fieldDataType, setFieldValueType,
			dateTimeVal, formDirector, fieldId
		)

		// Finally, let field widgets know a change has occurred so
		// their actors execute properly
		if (widgetView?.el?.className.includes('field-widget')) {
			widgetView.onChangeWidgetValue()
		}
	}

	/**
	 * Sets the field value for a widget based on the field data type and
	 * widget state.
	 * @param widget - The widget to be updated for the set field value action
	 * @param fieldDataType - The data type of the field to be updated
	 * @param setFieldValueType - The type of value to be set for the field
	 * @param formDirector - The form director for the form containing the widget
	 * @param fieldId - The ID of the field to be updated
	 * @param setFieldValue - The value to be set for the field
	 */
	setFieldValue(
		widget, fieldDataType, setFieldValueType,
		formDirector, fieldId, setFieldValue
	) {
		// Text-only fields handles parsing differently in various cases
		if (widget.state == TEXT_ONLY) {
			// #55932
			// text-only field-value-controls parse time differently
			// they use FieldValueFormatter.format() rather than date-time-util.js's
			// dateTimeToSplitFieldsObject function
			if (fieldDataType == TIME && setFieldValueType == CURRENT_DATE_TIME) {
				formDirector.setFieldValue(fieldId, new Date().toISOString())
			} else if (fieldDataType == PERCENT) {
				// #55931
				// Text-only percent fields do not shift their value properly
				// if a string format is used
				formDirector.setFieldValue(fieldId, parseFloat(setFieldValue))
			} else {
				formDirector.setFieldValue(fieldId, setFieldValue)
			}
		} else {
			// Adjust the field value saved in the form director with
			// the original value format
			console.log('field value before setFieldValue - ', formDirector.getFieldValue(fieldId))
			formDirector.setFieldValue(fieldId, setFieldValue)
			console.log('field value after setFieldValue - ', formDirector.getFieldValue(fieldId))
		}
	}

	/**
	 * Checks if the widget is a text-only DATE or DATE_TIME field and
	 * modifies the value to be sent so that it is in the appropriate
	 * format for the server. This is necessary because text-only fields
	 * are not read from the DOM and instead use the form director's
	 * value.
	 * @param widget - The widget to be updated for the set field value action
	 * @param fieldDataType - The data type of the field to be updated
	 * @param setFieldValueType - The type of value to be set for the field
	 * @param dateTimeVal - The date-time value to be set for the field
	 * @param formDirector - The form director for the form containing the widget
	 * @param fieldId - The ID of the field to be updated
	 */
	modifyTextOnlyDateValue(
		widget, fieldDataType, setFieldValueType,
		dateTimeVal, formDirector, fieldId
	) {
		// #55842
		// After the appropriately formatted DATE and DATE_TIME values are
		// set for display, we need to reformat the values for the server
		if (widget.state == TEXT_ONLY &&
			[DATE, DATE_TIME].includes(fieldDataType) &&
			setFieldValueType == CURRENT_DATE_TIME
		) {
			// This is necessary for text-only fields because readFromDom
			// does not pull text-only values and falls back on the
			// form director's value
			const localFormattedString =
				toZonelessISOFormat(dateTimeVal)

			if (fieldDataType == DATE) {
				// Date fields only need the date portion of the string (ex. 2023-12-31)
				formDirector.setFieldValue(
					fieldId,
					getDateString(localFormattedString)
				)
			} else if (fieldDataType == DATE_TIME) {
				formDirector.setFieldValue(fieldId, localFormattedString)
			}
		}
	}
}

/**
 * Takes a string in the format of YYYY-MM-DDTHH:MM:SS and returns
 * the date portion of the string (ex. 2023-12-31). If the string is
 * not in the expected format, the original string is returned.
 * @param dateTimeString - The date time string to be parsed
 * @returns {string} - The converted date string in format YYYY-MM-DD
 */
function getDateString(dateTimeString) {
	// Check input data type, so we don't try to split a non-string value
	if (dateTimeString && typeof dateTimeString === 'string') {
		return dateTimeString.split('T')[0]
	}
	return dateTimeString
}

/**
 * Takes an ISO string value and converts it into the date-time format
 * of YYYY-MM-DDTHH:MM:SS for use in the form director.
 * @param isoString - The ISO string to be converted
 * @param timeZone - The time zone to be used in the conversion. If not present
 * the local time zone is used.
 * @returns {string} - The converted date-time string in format
 * YYYY-MM-DDTHH:MM:SS
 */
function toZonelessISOFormat(isoString, timeZone = undefined) {
	// return the original string if it is null, undefined, empty, or not a string
	if (!isoString || typeof isoString !== 'string') {
		return isoString
	}

	const parsedDate = timeZone ?
		moment.tz(isoString, timeZone) : moment(isoString)
	if (!parsedDate.isValid()) {
		return isoString
	}

	return parsedDate.format('YYYY-MM-DDTHH:mm:ss')
}

export default SetFieldValueActor
export { toZonelessISOFormat }
export { getDateString }
SetFieldValueActor.__filename__ = 'forms/actions/set-field-value-actor.js'
